import { Container, Sprite, Texture } from 'pixi.js';
import type { AssetManager } from '../render/AssetManager';

interface Particle {
  sprite: Sprite;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
}

/**
 * Juni's death "poof": a one-shot burst of smoke puffs, ported from yknytt's
 * DeathParticles (4 emitters × 4 particles, death-0..3.png, spread 360°,
 * gravity drifting up, ~1.8s lifetime, fading out).
 */
export class DeathPoof {
  readonly container = new Container();
  private textures: Texture[] = [];
  private particles: Particle[] = [];

  constructor() {
    this.container.zIndex = 9; // just above Juni (z8)
  }

  async load(assets: AssetManager): Promise<void> {
    for (let i = 0; i < 4; i++) {
      const t = await assets.getTexture(`juni/particles/death-${i}.png`);
      if (t) this.textures.push(t);
    }
  }

  /** Emit the burst at a world position. */
  burst(x: number, y: number): void {
    if (this.textures.length === 0) return;
    for (const tex of this.textures) {
      for (let i = 0; i < 4; i++) {
        const sprite = new Sprite(tex);
        sprite.anchor.set(0.5, 0.5);
        sprite.x = x;
        sprite.y = y;
        const angle = Math.random() * Math.PI * 2;
        const speed = 16 * (0.5 + Math.random() * 0.5); // initial_velocity 16, random 0.5
        const maxLife = 1.8 * (1 - Math.random() * 0.25); // lifetime_randomness 0.25
        this.container.addChild(sprite);
        this.particles.push({
          sprite,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          life: maxLife,
          maxLife,
        });
      }
    }
  }

  update(dt: number): void {
    if (this.particles.length === 0) return;
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.life -= dt;
      if (p.life <= 0) {
        p.sprite.destroy();
        this.particles.splice(i, 1);
        continue;
      }
      p.vy += -25 * dt; // gravity (0,-25): drift upward
      p.sprite.x += p.vx * dt;
      p.sprite.y += p.vy * dt;
      p.sprite.alpha = Math.min(1, p.life / p.maxLife);
    }
  }

  clear(): void {
    for (const p of this.particles) p.sprite.destroy();
    this.particles = [];
  }

  destroy(): void {
    this.clear();
    this.container.destroy({ children: true });
  }
}
